import { keyedBin, matchesSortTrigger } from './sort.js';
/**
 * 4096 arrangements — 6 items over 4 bins, or 12 over 2 — which is instant and covers every sort
 * a teacher writes by hand. Past that the enumeration stops being free AND stops being
 * interesting: the trigger vocabulary covers at most a handful of arrangements per state, so a
 * bigger sort always has wrong answers left over unless someone authored thousands of
 * distractors. Above the limit this says "reachable" and marks itself non-exhaustive, which is
 * the honest direction — claiming a state is dead when nobody checked is the one answer that
 * would cost a teacher a card they wrote.
 */
const ENUMERATION_LIMIT = 4096;
const by = (states, trigger, reachable, cause) => {
    states.push(cause && !reachable ? { trigger, reachable, cause } : { trigger, reachable });
};
/** Every assignment of `items` to `bins`, as the `binId -> itemId[]` shape the evaluator reads. */
function arrangements(items, bins) {
    let out = [Object.fromEntries(bins.map(b => [b.id, []]))];
    for (const item of items) {
        const next = [];
        for (const partial of out) {
            for (const bin of bins) {
                const copy = {};
                for (const [k, v] of Object.entries(partial))
                    copy[k] = [...v];
                copy[bin.id] = [...(copy[bin.id] ?? []), item.id];
                next.push(copy);
            }
        }
        out = next;
    }
    return out;
}
export function sortReachability(d, 
/** The EFFECTIVE key — already resolved through any matched branch or scenario row. `null` is
 *  the coverage gap, where no arrangement is correct. */
key) {
    const states = [];
    const targeted = d.feedback.states.filter(s => s.type === 'targeted');
    if (d.gradingMode === 'optional') {
        by(states, 'any_response', true);
        for (const t of ['unanswered', 'incomplete_answer', 'correct', 'incorrect', 'any_answer'])
            by(states, t, false, 'mode');
        return { states, exhaustive: true };
    }
    /* Both unfinished states are reachable in every grading mode that evaluates at all: they are
       read off the placements before any key is consulted. */
    by(states, 'unanswered', true);
    by(states, 'incomplete_answer', d.items.length > 1);
    if (d.gradingMode === 'survey') {
        by(states, 'any_answer', d.items.length > 0 && d.bins.length > 0, d.items.length > 0 && d.bins.length > 0 ? undefined : 'nothing-to-pick');
        for (const t of ['correct', 'incorrect', 'any_response'])
            by(states, t, false, 'mode');
        return { states, exhaustive: true };
    }
    by(states, 'any_answer', false, 'mode');
    by(states, 'any_response', false, 'mode');
    /* No key to match: a coverage gap, no items, or no bins. Everything past the unfinished states
       dies with it, since correctness needs a complete key and the catch-all needs a full
       arrangement to fall through. */
    const complete = !!key && d.items.length > 0 && d.items.every(i => keyedBin(key, i.id) !== null);
    if (!complete || d.bins.length === 0) {
        by(states, 'correct', false, 'no-blanks');
        for (const s of targeted)
            by(states, s.trigger, false, 'no-blanks');
        by(states, 'incorrect', false, 'no-blanks');
        return { states, exhaustive: true };
    }
    // A complete key IS an arrangement, so Correct is always satisfiable from here.
    by(states, 'correct', true);
    const total = d.bins.length ** d.items.length;
    if (total > ENUMERATION_LIMIT) {
        for (const s of targeted)
            by(states, s.trigger, true);
        by(states, 'incorrect', true);
        return { states, exhaustive: false };
    }
    const all = arrangements(d.items, d.bins);
    const isCorrect = (p) => d.items.every(i => {
        const bin = Object.entries(p).find(([, ids]) => ids.includes(i.id))?.[0];
        return bin !== undefined && (key.anyBin?.includes(i.id)
            ? true
            : keyedBin(key, i.id) === bin || !!key.alsoAccepts?.[i.id]?.includes(bin));
    });
    const wrong = all.filter(p => !isCorrect(p));
    const covered = new Set();
    for (const s of targeted) {
        const hits = wrong.map((p, i) => [p, i])
            .filter(([p]) => matchesSortTrigger(s.trigger, p, key, d.items, d.bins));
        by(states, s.trigger, hits.length > 0, hits.length > 0 ? undefined : 'fully-covered');
        for (const [, i] of hits)
            covered.add(i);
    }
    /* What the catch-all still catches. Covered off entirely means a teacher's Default Incorrect
       card can never be seen, which is worth saying out loud. */
    by(states, 'incorrect', covered.size < wrong.length, covered.size < wrong.length ? undefined : 'fully-covered');
    return { states, exhaustive: true };
}
//# sourceMappingURL=sort-reachability.js.map